/**
 * The figures at the top of an employee's page (owner brief, 2026-09-21): is this person at work
 * now, when do they work next, how does the week stand. Pure, so the page only draws them.
 */
import type { ApiAbsence, ApiShift } from '@/api/data';
import { oslo, worked } from '@/rules';
import type { BreakPolicy } from '@timefly/core';

const at = (iso: string | null | undefined) => (iso ? Date.parse(iso) : Number.NaN);

/**
 * At work now: clocked in, not out, on a shift that started within the last 16 hours. A punch
 * left open for days is a forgotten punch-out, which Timeliste shows, not somebody at work.
 */
export function onDuty(mine: ApiShift[], now: number): ApiShift | null {
  return (
    mine.find((s) => s.punch_in_time && !s.punch_out_time && now - at(s.punch_in_time) < 16 * 3600_000) ??
    null
  );
}

/** The next shift that has not started, or the one running now if nobody has clocked out of it. */
export function nextShift(mine: ApiShift[], now: number): ApiShift | null {
  return (
    [...mine]
      .filter((s) => at(s.start_time) > now || (at(s.start_time) <= now && at(s.end_time) > now))
      .sort((a, b) => a.start_time.localeCompare(b.start_time))[0] ?? null
  );
}

/** Planned length in hours, start to end; the card's "planlagt" line, not a pay figure. */
const planned = (s: ApiShift) => Math.max(0, (at(s.end_time) - at(s.start_time)) / 3600_000);

/**
 * The week from Monday: hours worked after break (both punches only, as payroll counts them)
 * and hours planned. The agreed week is the person's own number; none, or 0 and below, is
 * "no agreed week" rather than a denominator (4 active people carry 0 or less, 2026-09-21).
 */
export function weekFigures(
  mine: ApiShift[],
  monday: string,
  sunday: string,
  policy: BreakPolicy | null,
  agreed: number | null | undefined,
) {
  const inWeek = mine.filter((s) => {
    const d = oslo(s.start_time)?.date ?? '';
    return d >= monday && d <= sunday;
  });
  const workedH = inWeek.reduce((a, s) => a + (worked(s, policy) ?? 0), 0);
  const plannedH = inWeek.reduce((a, s) => a + planned(s), 0);
  const target = agreed != null && agreed > 0 ? agreed : null;
  return {
    worked: workedH,
    planned: plannedH,
    target,
    pct: target ? Math.min(100, (workedH / target) * 100) : 0,
  };
}

/** Pending requests and the day the latest one was sent (created_at), in Oslo. */
export function requests(abs: ApiAbsence[]) {
  const pending = abs.filter((a) => a.status === 'pending').length;
  const sent = abs
    .map((a) => a.created_at ?? '')
    .filter(Boolean)
    .sort();
  const last = sent[sent.length - 1];
  return { pending, latest: last ? (oslo(last)?.date ?? null) : null };
}

/** Absence still ahead or running, soonest first. */
export function upcomingAbsence(abs: ApiAbsence[], today: string): ApiAbsence[] {
  return abs
    .filter((a) => (oslo(a.end_date)?.date ?? '') >= today && a.status !== 'declined')
    .sort((a, b) => a.start_date.localeCompare(b.start_date));
}

/** "8 t 03 m": worked time the way a person reads a timesheet line. */
export const hm = (h: number | null) => {
  if (h == null) return '–';
  const m = Math.round(h * 60);
  return `${Math.floor(m / 60)} t ${String(m % 60).padStart(2, '0')} m`;
};
